// web/src/routes/MatchAdd.tsx
// Stage 1 of the two-stage match flow (2026-05-19 split):
//
//   1. MatchAdd       — the SETUP. Match logistics that are known BEFORE
//                       kickoff. POSTs a real match record with empty
//                       events; routes onward to MatchRecord.
//   2. MatchRecord    — the EVENTS + RESULT. Tap-on-pitch + final score +
//                       MoM + note. PATCHes the match. Fires the Full-Time
//                       reveal at the end.
//
// Rationale: cramming setup + live recording into one giant form was the
// weakest part of the UX. Setup is logistics ("which match is this?"),
// event recording is theatre ("what happened?"). Splitting them lets a
// parent set up a match from the couch and live-record from the touchline.

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FormField } from '../components/FormField';
import { Button } from '../components/Button';
import { useMatchApi, type CreateMatchInput } from '../api/match';
import { ApiError } from '../api/client';
import { PlanCapReached } from '../components/PlanCapReached';
import {
  MATCH_TYPES,
  MATCH_FORMATS,
  type MatchType,
  type MatchFormat,
} from '../types';
import {
  Section,
  Field,
  ChipRow,
  MultiChipRow,
  Toggle,
  MATCH_POSITIONS,
} from '../components/MatchFormBits';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function MatchAdd() {
  const { kidId } = useParams<{ kidId: string }>();
  const navigate = useNavigate();
  const api = useMatchApi();

  // Setup fields only — score, events, MoM, rating, subs, note all live
  // in MatchRecord (stage 2). Captain stays here because it's typically
  // known pre-kickoff (team sheet).
  const [date, setDate] = useState(todayIso());
  const [kickoff, setKickoff] = useState('');
  const [opposition, setOpposition] = useState('');
  const [homeAway, setHomeAway] = useState<'home' | 'away' | ''>('');
  const [matchType, setMatchType] = useState<MatchType>('league');
  const [matchFormat, setMatchFormat] = useState<MatchFormat | ''>('');
  const [matchTitle, setMatchTitle] = useState('');
  const [matchLengthMin, setMatchLengthMin] = useState<string>('60');
  const [positions, setPositions] = useState<string[]>([]);
  const [captain, setCaptain] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  // F1: when the server returns plan_limit_reached on the matches cap,
  // we swap the form for a full-screen celebration + upgrade screen
  // instead of a tiny error toast. The form mounts behind it, so
  // "Maybe later" returns the parent to their typed-out match shell.
  const [capHit, setCapHit] = useState(false);

  if (!kidId) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!date) next.date = 'Required.';
    const lengthN = Number(matchLengthMin);
    if (!Number.isInteger(lengthN) || lengthN < 1 || lengthN > 200) {
      next.matchLengthMin = 'Must be 1–200.';
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    const payload: CreateMatchInput = {
      date,
      matchLengthMin: lengthN,
      matchType,
    };
    if (kickoff) payload.kickoff = kickoff;
    if (opposition.trim()) payload.opposition = opposition.trim();
    if (homeAway) payload.homeAway = homeAway;
    if (matchTitle.trim()) payload.matchTitle = matchTitle.trim();
    if (matchFormat) payload.matchFormat = matchFormat;
    if (positions.length) payload.positions = positions;
    if (captain) payload.captain = true;

    setSubmitting(true);
    try {
      const res = await api.create(kidId, payload);
      // Stage 1 done. Hand off to MatchRecord. No reveal here — the
      // Full-Time reveal belongs at the END of stage 2 (when the match
      // is "complete"), not at the moment the shell is created.
      navigate(
        `/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(res.match.matchId)}/record`
      );
    } catch (err) {
      const e2 = err as ApiError;
      // F1: hit the matches-per-season cap → full-screen celebration +
      // upgrade screen, NOT a small red error. Other 403s (e.g.
      // captcha_failed, account_suspended) fall through to inline.
      if (e2.status === 403 && e2.code === 'plan_limit_reached') {
        setCapHit(true);
      } else if (e2.status === 403) {
        setErrors({
          form:
            e2.message ||
            "You've reached your free-plan match limit for this season. Upgrade for more.",
        });
      } else {
        setErrors(e2.fields ?? { form: e2.message || 'Could not save the match.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {capHit ? (
        <PlanCapReached cap="matches" onDismiss={() => setCapHit(false)} />
      ) : null}
    <form onSubmit={submit}>
      <Link
        to={`/kids/${encodeURIComponent(kidId)}`}
        style={{ color: 'var(--muted)', fontSize: 14 }}
      >
        ← Cancel
      </Link>
      <h1 style={{ marginTop: 8, marginBottom: 8 }}>Set up the match</h1>
      <p style={{ color: 'var(--muted)', marginBottom: 24, fontSize: 14 }}>
        Step 1 of 2 — the logistics. You'll record events + the score in the next step.
      </p>

      <Section title="Match info">
        <FormField
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          max={todayIso()}
          error={errors.date}
          required
        />
        <FormField
          label="Kick-off (optional)"
          type="time"
          value={kickoff}
          onChange={(e) => setKickoff(e.target.value)}
        />
        <FormField
          label="Opposition"
          value={opposition}
          onChange={(e) => setOpposition(e.target.value)}
          placeholder="e.g. Histon U9s"
          error={errors.opposition}
        />

        <Field label="Match type">
          <ChipRow
            value={matchType}
            options={MATCH_TYPES}
            onChange={(v) => setMatchType(v as MatchType)}
          />
        </Field>

        <Field label="Format (optional)">
          <ChipRow
            value={matchFormat}
            options={MATCH_FORMATS}
            onChange={(v) => setMatchFormat(v as MatchFormat | '')}
            allowClear
          />
        </Field>

        <FormField
          label="Match title (optional)"
          value={matchTitle}
          onChange={(e) => setMatchTitle(e.target.value)}
          hint="e.g. U9 Cup Final"
        />

        <FormField
          label="Match length (minutes)"
          type="number"
          min={1}
          max={200}
          value={matchLengthMin}
          onChange={(e) => setMatchLengthMin(e.target.value)}
          error={errors.matchLengthMin}
          required
        />

        <Field label="Home / away">
          <ChipRow
            value={homeAway}
            options={['home', 'away']}
            onChange={(v) => setHomeAway(v as 'home' | 'away' | '')}
            allowClear
          />
        </Field>
      </Section>

      <Section title="Position">
        <Field label="Positions played (tap any that apply — they can rotate)">
          <MultiChipRow
            values={positions}
            options={MATCH_POSITIONS}
            onChange={setPositions}
            max={5}
          />
        </Field>
        <Toggle label="Wearing the armband (captain)" value={captain} onChange={setCaptain} />
      </Section>

      {errors.form ? (
        <p style={{ color: 'var(--error)', fontSize: 14 }}>{errors.form}</p>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 20 }}>
        <Button
          label={submitting ? 'Saving…' : 'Continue to events →'}
          type="submit"
          loading={submitting}
        />
        <Link
          to={`/kids/${encodeURIComponent(kidId)}`}
          style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 14, padding: 8 }}
        >
          Cancel
        </Link>
      </div>
    </form>
    </>
  );
}
